import { LabelObsStudy } from "../../api/LabelizeApi";
import { EvalAiMode, EvalWorkflow } from "../../api/EvalWorkflowApi";
import i18n from "i18next";

export class ViewerControl {
  constructor(
    public name: string,
    protected _tooltip: string,
    public controlEnabled: boolean,
    public controlDisplayed: boolean,
    public annotationDisplayed: boolean,
    protected _onNextStudy: (control: ViewerControl) => ViewerControl = (control) => control
  ) {}

  static withAi(
    controlEnabled: boolean,
    controlDisplayed: boolean,
    annotationDisplayed: boolean,
    onNextStudy: (control: ViewerControl) => ViewerControl = (control) => control
  ): ViewerControl {
    return new ViewerControl(
      "AI",
      i18n.t("tool.aiRegions"),
      controlEnabled,
      controlDisplayed,
      annotationDisplayed,
      onNextStudy
    );
  }

  static withPasses(b: boolean): ViewerControl {
    return new ViewerControl("Pass", i18n.t("tool.passes"), b, b, b);
  }

  static withObservationsControlHidden(): ViewerControl {
    return new ViewerControl("Obs", i18n.t("tool.observations"), false, false, true);
  }

  static withObservations(b: boolean): ViewerControl {
    return new ViewerControl("Obs", i18n.t("tool.observations"), b, b, b);
  }

  static withCrossCursor(controlDisplayed: boolean): ViewerControl {
    return new ViewerControl("Cursor", i18n.t("tool.crossCursor"), controlDisplayed, controlDisplayed, false);
  }

  onNextStudy(): ViewerControl {
    return this._onNextStudy(this);
  }

  get tooltip(): string {
    return this.annotationDisplayed
      ? `${i18n.t("global.hide")} ${this._tooltip}`
      : `${i18n.t("global.display")} ${this._tooltip}`;
  }

  toggle(): ViewerControl {
    if (!this.controlEnabled) {
      return this;
    }
    return new ViewerControl(
      this.name,
      this._tooltip,
      this.controlEnabled,
      this.controlDisplayed,
      !this.annotationDisplayed,
      this._onNextStudy
    );
  }

  hideAnnotation(): ViewerControl {
    if (!this.controlEnabled) {
      return this;
    }
    return new ViewerControl(
      this.name,
      this._tooltip,
      this.controlEnabled,
      this.controlDisplayed,
      false,
      this._onNextStudy
    );
  }

  showAnnotation(): ViewerControl {
    if (!this.controlEnabled) {
      return this;
    }
    return new ViewerControl(
      this.name,
      this._tooltip,
      this.controlEnabled,
      this.controlDisplayed,
      true,
      this._onNextStudy
    );
  }

  enable(): ViewerControl {
    return new ViewerControl(
      this.name,
      this._tooltip,
      true,
      this.controlDisplayed,
      this.annotationDisplayed,
      this._onNextStudy
    );
  }

  disable(): ViewerControl {
    return new ViewerControl(
      this.name,
      this._tooltip,
      false,
      this.controlDisplayed,
      this.annotationDisplayed,
      this._onNextStudy
    );
  }
}

class ConfidenceControl {
  constructor(public controlEnabled: boolean, public controlDisplayed: boolean, public value: number) {}

  at(confidence: number): ConfidenceControl {
    return new ConfidenceControl(this.controlEnabled, this.controlDisplayed, confidence);
  }
}

export class DisplayMode {
  static labelize(study: LabelObsStudy): DisplayMode {
    const withAiWithoutTimer = study.showAiRegions && !study.enablingAiWaitSecond;
    return new DisplayMode(
      ViewerControl.withAi(withAiWithoutTimer, study.showAiRegions, withAiWithoutTimer, (control) =>
        withAiWithoutTimer ? control : control.hideAnnotation().disable()
      ),
      ViewerControl.withPasses(false),
      ViewerControl.withObservations(true),
      new ConfidenceControl(withAiWithoutTimer, withAiWithoutTimer, 0),
      study.enablingAiWaitSecond || 0,
      ViewerControl.withCrossCursor(true)
    );
  }

  static demo(): DisplayMode {
    return new DisplayMode(
      ViewerControl.withAi(true, true, false, (control) => control.hideAnnotation()),
      ViewerControl.withPasses(false),
      ViewerControl.withObservations(false),
      new ConfidenceControl(false, false, 50),
      0,
      ViewerControl.withCrossCursor(false)
    );
  }

  static studyDetail(): DisplayMode {
    return new DisplayMode(
      ViewerControl.withAi(true, true, true),
      ViewerControl.withPasses(true),
      ViewerControl.withObservations(true),
      new ConfidenceControl(true, true, 0),
      0,
      ViewerControl.withCrossCursor(true)
    );
  }

  static readerWorkflow(wkf: EvalWorkflow, aiMode: EvalAiMode): DisplayMode {
    return new DisplayMode(
      ViewerControl.withAi(
        wkf === EvalWorkflow.RD_AI && aiMode === EvalAiMode.VIEWER,
        wkf === EvalWorkflow.RD_AI && aiMode === EvalAiMode.VIEWER,
        false,
        (control) => control.hideAnnotation()
      ),
      ViewerControl.withPasses(false),
      ViewerControl.withObservationsControlHidden(),
      new ConfidenceControl(false, false, 0),
      0,
      ViewerControl.withCrossCursor(false)
    );
  }

  static gtWorkflow(): DisplayMode {
    return new DisplayMode(
      ViewerControl.withAi(false, false, false),
      ViewerControl.withPasses(false),
      ViewerControl.withObservations(true),
      new ConfidenceControl(false, false, 0),
      0,
      ViewerControl.withCrossCursor(true)
    );
  }

  static gtReconcileWorkflow(): DisplayMode {
    const gt = this.gtWorkflow();
    return new DisplayMode(gt.ai, ViewerControl.withPasses(true), gt.obs, gt.confidence, 0, gt.cursorControl);
  }

  constructor(
    public ai: ViewerControl,
    public pass: ViewerControl,
    public obs: ViewerControl,
    public confidence: ConfidenceControl,
    public timerBeforeAiEnabled = 0,
    public cursorControl: ViewerControl
  ) {}

  onNextStudy(): DisplayMode {
    return new DisplayMode(
      this.ai.onNextStudy(),
      this.pass.onNextStudy(),
      this.obs.onNextStudy(),
      this.confidence,
      this.timerBeforeAiEnabled,
      this.cursorControl.onNextStudy()
    );
  }

  get controlsDisplayed(): number {
    return [this.ai.controlDisplayed, this.pass.controlDisplayed, this.obs.controlDisplayed].filter(
      (value) => value === true
    ).length;
  }

  get allDisplayedButtonsChecked(): boolean {
    return (
      (!this.ai.controlDisplayed || this.ai.annotationDisplayed) &&
      (!this.pass.controlDisplayed || this.pass.annotationDisplayed) &&
      (!this.obs.controlDisplayed || this.obs.annotationDisplayed)
    );
  }

  get allDisplayedButtonsEnabled(): boolean {
    return (
      (!this.ai.controlDisplayed || this.ai.controlEnabled) &&
      (!this.pass.controlDisplayed || this.pass.controlEnabled) &&
      (!this.obs.controlDisplayed || this.obs.controlEnabled)
    );
  }

  get confidenceThreshold(): number {
    return this.confidence.value;
  }

  updateConfidence(confidence: number): DisplayMode {
    return new DisplayMode(
      this.ai,
      this.pass,
      this.obs,
      this.confidence.at(confidence),
      this.timerBeforeAiEnabled,
      this.cursorControl
    );
  }

  enableAiControl(): DisplayMode {
    return new DisplayMode(
      this.ai.enable(),
      this.pass,
      this.obs,
      this.confidence,
      this.timerBeforeAiEnabled,
      this.cursorControl
    );
  }

  public toggleAi(): DisplayMode {
    return new DisplayMode(
      this.ai.toggle(),
      this.pass,
      this.obs,
      this.confidence,
      this.timerBeforeAiEnabled,
      this.cursorControl
    );
  }

  public togglePasses(): DisplayMode {
    return new DisplayMode(
      this.ai,
      this.pass.toggle(),
      this.obs,
      this.confidence,
      this.timerBeforeAiEnabled,
      this.cursorControl
    );
  }

  public toggleObs(): DisplayMode {
    return new DisplayMode(
      this.ai,
      this.pass,
      this.obs.toggle(),
      this.confidence,
      this.timerBeforeAiEnabled,
      this.cursorControl
    );
  }

  public toggleCursor(): DisplayMode {
    return new DisplayMode(
      this.ai,
      this.pass,
      this.obs,
      this.confidence,
      this.timerBeforeAiEnabled,
      this.cursorControl.toggle()
    );
  }

  public toggleAll(): DisplayMode {
    if (this.allDisplayedButtonsChecked) {
      return new DisplayMode(
        this.ai.hideAnnotation(),
        this.pass.hideAnnotation(),
        this.obs.hideAnnotation(),
        this.confidence,
        this.timerBeforeAiEnabled,
        this.cursorControl
      );
    } else {
      return new DisplayMode(
        this.ai.showAnnotation(),
        this.pass.showAnnotation(),
        this.obs.showAnnotation(),
        this.confidence,
        this.timerBeforeAiEnabled,
        this.cursorControl
      );
    }
  }
}
